import { Controller } from "@hotwired/stimulus"
import { PdfInlineViewer } from "../lib/pdf_inline_viewer"
import { watchOutsHtml, paintWatchOutsCard } from "../lib/contract_watch_outs"

// Sending → Upload (Phelipe 2026-10-23): the contract page shows the PDF
// they uploaded — the same viewer and drag/resize boxes as a received
// contract — and, while it's a draft, the sender places where each side
// signs: their own signature / date / name, the other side's, and fixed text.
// Spots autosave onto the draft; the workspace's send modal sends them along
// and the executed PDF stamps each person's details on their own line.
const PARTY_STYLE = {
  sender:       { color: "#c2410c", bg: "rgba(255,247,237,.92)" },
  counterparty: { color: "#0369a1", bg: "rgba(240,249,255,.92)" },
  none:         { color: "#475569", bg: "rgba(248,250,252,.94)" }
}

export default class extends Controller {
  static targets = ["viewer", "zoomBar", "zoomLabel", "list", "empty", "status", "saveStatus", "placeBtn",
                    "watchOuts", "watchOutsCard"]
  static values = { pdfUrl: String, saveUrl: String, fields: Array, editable: Boolean, labels: Object,
                    summaryUrl: String }

  connect() {
    this._fields = (this.fieldsValue || []).map((f, i) => this._decorate({ ...f, id: `s${i}${Date.now()}` }))
    this._paintList()
    if (this.hasViewerTarget && this.pdfUrlValue) {
      this._viewer = new PdfInlineViewer(this.viewerTarget, this.pdfUrlValue)
      this._viewer.onZoom = z => this._paintZoom(z)
      this._viewer.onFieldChange = (id, p) => {
        const f = this._fields.find(x => x.id === id)
        if (f) { Object.assign(f, p); this._changed() }
      }
      this._viewer.load()
        .then(() => {
          this._fields.forEach(f => this._viewer.addField(f))
          if (!this.editableValue) this._viewer.setLocked(true)
          this._showZoomBar(true)
        })
        .catch(() => this._iframeFallback())
    }
    // Watch-outs (Phelipe 2026-10-23): the same one AI read of the PDF the
    // received page uses, from the sender's side. Only with AI on (the view
    // leaves the URL out otherwise).
    if (this.hasSummaryUrlValue && this.summaryUrlValue && this.hasWatchOutsTarget) this._loadSummary()
    // An unsaved move survives closing the tab (keepalive save).
    this._flushBound = (e) => { if (e.type === "pagehide" || document.visibilityState === "hidden") this.flush(true) }
    window.addEventListener("pagehide", this._flushBound)
    document.addEventListener("visibilitychange", this._flushBound)
  }

  disconnect() {
    window.removeEventListener("pagehide", this._flushBound)
    document.removeEventListener("visibilitychange", this._flushBound)
    clearTimeout(this._saveTimer)
    clearTimeout(this._summaryTimer)
  }

  // ── Watch-outs ──────────────────────────────────────────────────────────
  // The first call starts the read in the background; later calls poll
  // until it lands (big PDFs take a little while). Failures stay quiet —
  // the card simply never shows.
  async _loadSummary() {
    this._summaryTries = (this._summaryTries || 0) + 1
    if (this._summaryTries > 40) return
    try {
      const resp = await fetch(this.summaryUrlValue, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await resp.json().catch(() => ({}))
      if (data.status === "done" && data.result) return this._renderWatchOuts(data.result)
      if (data.status === "unavailable" || data.status === "failed" || !resp.ok) return
    } catch (_) { /* poll again */ }
    this._summaryTimer = setTimeout(() => this._loadSummary(), 4000)
  }

  _renderWatchOuts(result) {
    const html = watchOutsHtml(result?.watch_outs, "contract-pdf-spots", t => this._esc(t))
    paintWatchOutsCard(this.hasWatchOutsCardTarget ? this.watchOutsCardTarget : null, this.watchOutsTarget, html)
  }

  jumpWatchOut(e) {
    const btn = e.currentTarget
    this._viewer?.highlight(parseInt(btn.dataset.page, 10) || 1, btn.dataset.quote || "")
  }

  // ── Placing ─────────────────────────────────────────────────────────────
  place(e) {
    if (e) e.preventDefault()
    if (!this.editableValue) return
    if (!this._viewer) return this._say(this._label("viewerFailed"))
    const btn = e.currentTarget
    const kind = btn.dataset.kind
    const party = btn.dataset.party || null
    // Clicking the same button again cancels.
    if (this._armed === btn) return this._disarm()
    this._disarm()
    this._armed = btn
    btn.classList.add("border-brand", "text-brand", "bg-brand/5")
    this._say(this._label("clickNow"))
    this._viewer.setPlaceKind("field")
    this._viewer.enablePlacement(spot => {
      const field = this._decorate({
        id: `f${Date.now()}${Math.floor(Math.random() * 1000)}`,
        kind, party, page: spot.page, x: spot.x, y: spot.y, size: 1, text: ""
      })
      this._fields.push(field)
      this._viewer.addField(field)
      this._disarm()
      this._say("")
      this._paintList()
      this._changed()
      if (kind === "text") this.listTarget.querySelector(`[data-field-id="${field.id}"] input`)?.focus()
    })
  }

  _disarm() {
    if (this._armed) this._armed.classList.remove("border-brand", "text-brand", "bg-brand/5")
    this._armed = null
    this._viewer?.disablePlacement()
  }

  removeField(e) {
    if (e) e.preventDefault()
    const id = e.currentTarget.closest("[data-field-id]")?.dataset.fieldId
    if (!id) return
    this._fields = this._fields.filter(f => f.id !== id)
    this._viewer?.removeField(id)
    this._paintList()
    this._changed()
  }

  textChanged(e) {
    const id = e.target.closest("[data-field-id]")?.dataset.fieldId
    const f = this._fields.find(x => x.id === id)
    if (!f) return
    f.text = e.target.value
    f.label = f.text || this._label("text")
    this._viewer?.updateField(id, { text: f.text, label: f.label })
    this._changed()
  }

  zoomIn() { this._viewer?.zoomIn() }
  zoomOut() { this._viewer?.zoomOut() }

  // ── What the workspace's send modal reads ───────────────────────────────
  fieldsForSave() {
    return this._fields.map(f => ({
      kind: f.kind, party: f.party || null, page: f.page,
      x: Number(Number(f.x).toFixed(1)), y: Number(Number(f.y).toFixed(1)),
      size: f.size || 1, text: f.kind === "text" ? (f.text || "") : ""
    }))
  }

  // One line for the send pop-up: where the signatures land, or that none
  // were placed (they then ride the certificate page at the end).
  summaryText() {
    const sigs = this._fields.filter(f => f.kind === "signature")
    if (!sigs.length) return { text: this._label("sendNone"), warn: true }
    const list = sigs.map(f => `${f.label} (${this._label("page").replace("%{page}", f.page)})`).join(", ")
    const missingThem = !sigs.some(f => f.party === "counterparty")
    return { text: this._label("sendList").replace("%{list}", list) + (missingThem ? ` ${this._label("sendNoThem")}` : ""), warn: missingThem }
  }

  // Save now (Save as Draft, Send, or leaving the page).
  flush(keepalive = false) {
    clearTimeout(this._saveTimer)
    if (!this.editableValue || !this.saveUrlValue || !this._dirty) return Promise.resolve()
    this._dirty = false
    this._saveState("saving")
    const req = fetch(this.saveUrlValue, {
      method: "POST", credentials: "same-origin", keepalive,
      headers: { "Content-Type": "application/json", "Accept": "application/json",
                 "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify({ signature_fields: this.fieldsForSave() })
    })
    if (keepalive) return Promise.resolve()
    return req.then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(() => this._saveState("saved"))
      .catch(() => { this._dirty = true; this._saveState("error") })
  }

  _changed() {
    this._dirty = true
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this.flush(), 700)
  }

  _saveState(state) {
    if (!this.hasSaveStatusTarget) return
    clearTimeout(this._saveStateTimer)
    this.saveStatusTarget.textContent = state === "saving" ? this._label("saving")
      : state === "saved" ? this._label("saved")
      : state === "error" ? this._label("saveFailed") : ""
    this.saveStatusTarget.classList.toggle("text-red-600", state === "error")
    if (state === "saved") this._saveStateTimer = setTimeout(() => { this.saveStatusTarget.textContent = "" }, 2500)
  }

  // ── Painting ────────────────────────────────────────────────────────────
  // Colour says whose box it is (orange = you, blue = them, gray = text) and
  // the label on the paper says what it is.
  _decorate(f) {
    const style = PARTY_STYLE[f.party] || PARTY_STYLE.none
    const key = f.kind === "text" ? "text" : `${f.party === "sender" ? "your" : "their"}_${f.kind}`
    const label = f.kind === "text" ? (f.text || this._label("text")) : this._label(key)
    return { ...f, color: style.color, bg: style.bg, label }
  }

  _paintList() {
    if (!this.hasListTarget) return
    const esc = t => this._esc(t)
    this.listTarget.innerHTML = this._fields.map(f => {
      const dot = `<span class="w-2 h-2 rounded-full shrink-0" style="background:${f.color}"></span>`
      const what = f.kind === "text" && this.editableValue
        ? `<input type="text" value="${esc(f.text || "")}" placeholder="${esc(this._label("textPlaceholder"))}"
                  data-action="input->contract-pdf-spots#textChanged"
                  class="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1 text-[13px]">`
        : `<span class="flex-1 min-w-0 truncate text-gray-700">${esc(f.label)}</span>`
      const remove = this.editableValue
        ? `<button type="button" data-action="click->contract-pdf-spots#removeField" aria-label="${esc(this._label("remove"))}"
                   class="text-gray-300 hover:text-red-500 text-[15px] leading-none px-1 cursor-pointer">&times;</button>`
        : ""
      return `<div class="flex items-center gap-1.5 text-[13px]" data-field-id="${esc(f.id)}">${dot}${what}
                <span class="text-gray-400 shrink-0">${esc(this._label("page").replace("%{page}", f.page))}</span>${remove}</div>`
    }).join("")
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", this._fields.length > 0)
  }

  _iframeFallback() {
    this._viewer = null
    this._showZoomBar(false)
    this.viewerTarget.innerHTML = `<iframe src="${this._esc(this.pdfUrlValue)}" class="w-full h-full" style="border:0;min-height:480px;"></iframe>`
    if (this.editableValue) this._say(this._label("viewerFailed"))
  }

  _showZoomBar(show) {
    if (!this.hasZoomBarTarget) return
    this.zoomBarTarget.classList.toggle("hidden", !show)
    this.zoomBarTarget.classList.toggle("flex", show)
  }

  _paintZoom(zoom) {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(zoom * 100)}%`
  }

  _say(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text || ""
    this.statusTarget.classList.toggle("hidden", !text)
  }

  _label(key) {
    const labels = this.labelsValue || {}
    return labels[key] ?? key
  }

  _esc(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML.replace(/"/g, "&quot;")
  }
}
